import { useMemo } from 'react';

import { isValidCageId } from '@/domain/cages';
import { useCageController_Overview } from '@/services/hooks';

import { CAGE_OVERVIEW_STALE_TIME_MS, cageOverviewQueryParams } from './cageOverviewQuery';
import { mapCageOverviewToDomain } from './overview.mappers';
import type {
    UseGetCageOverviewOptions,
    UseGetCageOverviewParams,
    UseGetCageOverviewResult,
} from './overview.types';

/**
 * The unit every overview weight is in. Views label and export overview values with
 * this, never with the cage filter's `weightUnitTag`, which the overview ignores.
 */
export const OVERVIEW_WEIGHT_UNIT_TAG = 'g';

/**
 * Fetches the overview for a cage: weight estimates, camera telemetry, swim
 * speed and the latest hourly environmental snapshot, each in its own block.
 *
 * `data` is never null — an absent response goes through the same mapper as a
 * present one, so consumers read `data.biomass.days` without guarding. Request
 * state lives in `isLoading` and `error`.
 *
 * `options.enabled` can only narrow when the query runs, never widen it: the cage
 * id has to be valid regardless, or an invalid one would go out as cage 0 and
 * come back as an error or, worse, a plausible empty overview.
 *
 * Weights are always in grams. A view that needs another unit converts on the way out
 * (`utils/weightConverter.ts`) rather than asking the backend for it.
 */
export const useGetCageOverview = (
    cageId: number | undefined,
    params: UseGetCageOverviewParams,
    options?: UseGetCageOverviewOptions
): UseGetCageOverviewResult => {
    const hasValidCageId = cageId != null && isValidCageId(cageId);

    const overviewQuery = useCageController_Overview(cageId ?? 0, cageOverviewQueryParams(params), {
        staleTime: CAGE_OVERVIEW_STALE_TIME_MS,
        ...options,
        // Applied after the spread and combined rather than replaced, so a
        // caller cannot enable a request for an invalid cage id.
        enabled: hasValidCageId && (options?.enabled ?? true),
    });

    const data = useMemo(
        () => mapCageOverviewToDomain(overviewQuery.data?.data),
        [overviewQuery.data?.data]
    );

    return {
        ...overviewQuery,
        isLoading: overviewQuery.isPending,
        data,
    };
};
